// Dependencies
import React, { useEffect } from "react";

// hooks
import useRequest from "../../../../../../../Hooks/useRequest";
import { useDispatch } from "react-redux";
import { getReviewReportColumns } from "./columns";

// components
import Input from "../../../../../../../Components/Inputs/Text";
import DeletedObjectWarningModal from "../../DeletedObjectWarningModal";
import DeletedObjectConfirmationModal from "../../DeletedObjectConfirmationModal";
import Table from "../../../../../../../Components/TableLite";

// styles
import css from "../../DeletedObjects.styl";

const DeletedReviewReports = (props) => {
	const {
		companyID,
		filterState,
		tableData,
		setTableData,
		deletedObjectWarningModalState,
		deletedObjectConfirmationModalState,
		handleChange,
		setDeletedObjectWarningModalState,
		setDeletedObjectConfirmationModalState,
		isLoading,
		data,
	} = props;

	const useUndeleteRequestResponse = useRequest(
		"UndeleteReviewReport",
		{ filters: { companyID, reviewReportID: deletedObjectConfirmationModalState.deletedObjectId } },
		{ manual: true }
	);

	const confirmUndeleteReviewReport = (reviewReportRow) => {
		setDeletedObjectConfirmationModalState({
			displayDeletedObjectConfirmationModal: true,
			deletedObjectId: reviewReportRow.id,
		});
	};

	/**
	 * Undeletes the report
	 */
	const undeleteReviewReport = async () => {
		// Remove the row from display in the table
		const newTableData = tableData.filter((row) => row.id !== deletedObjectConfirmationModalState.deletedObjectId);
		setTableData(newTableData);

		// Call the lambda function to perform the undelete in the db
		useUndeleteRequestResponse.revalidate();

		// Hides the confirmation modal
		setDeletedObjectConfirmationModalState({
			...deletedObjectConfirmationModalState,
			displayDeletedObjectConfirmationModal: false,
		});
	};

	const columns = getReviewReportColumns({ confirmUndeleteReviewReport });

	const dispatch = useDispatch();

	/**
	 * Process the result of the Undelete Lambda Function
	 */
	useEffect(() => {
		if (useUndeleteRequestResponse.data) {
			const undeleteResult = useUndeleteRequestResponse.data.get("undeleteResult");
			const parsedResult = JSON.parse(undeleteResult);

			const statusCode = parsedResult.statusCode;
			const parsedBody = JSON.parse(parsedResult.body);

			// If something is wrong, pop up a toast notification
			if (statusCode !== 200 || parsedBody.status === "Error") {
				// Put the row back in the table
				setTableData(data ? data.deletedObjectCollectionData.deletedObjects : []);
				// Show the message from the server if appropriate
				if (parsedBody.message.search("has no parent review and is not permitted to be undeleted.") !== -1) {
					setDeletedObjectWarningModalState({
						displayDeletedObjectWarningModal: true,
						message:
							"Unable to undelete this review report (review form) as the parent review is currently deleted.",
					});
				} else {
					dispatch({
						type: "[100] create info toast",
						message:
							"Oops! Something has gone wrong with the undelete request. Please contact the development team.",
						actionMessage: "Dismiss",
					});
				}
			}
		}
	}, [useUndeleteRequestResponse.data]);

	return (
		<>
			<div className={css.filters}>
				<div className={css.filtersContainer}>
					<div className={css.filterBox}>
						<label>ID</label>
						<div className={css.filterInput}>
							<Input value={filterState.id} onChange={(value) => handleChange("id", value)} />
						</div>
					</div>

					<div className={css.filterBox}>
						<label>Review ID</label>
						<div className={css.filterInput}>
							<Input value={filterState.reviewId} onChange={(value) => handleChange("reviewId", value)} />
						</div>
					</div>

					<div className={css.filterBox}>
						<label>Form Name</label>
						<div className={css.filterInput}>
							<Input value={filterState.name} onChange={(value) => handleChange("name", value)} />
						</div>
					</div>

					<div className={css.filterBox}>
						<label>Subject</label>
						<div className={css.filterInput}>
							<Input value={filterState.subject} onChange={(value) => handleChange("subject", value)} />
						</div>
					</div>

					<div className={css.filterBox}>
						<label>Author Name</label>
						<div className={css.filterInput}>
							<Input value={filterState.author} onChange={(value) => handleChange("author", value)} />
						</div>
					</div>

					<div className={css.filterBox}>
						<label>Signer Name</label>
						<div className={css.filterInput}>
							<Input value={filterState.signer} onChange={(value) => handleChange("signer", value)} />
						</div>
					</div>
				</div>
			</div>

			<div className={css.results}>
				<div className={css.tableContainer}>
					<div className={css.tableWrapper}>
						<Table
							isLoading={isLoading || !Array.isArray(tableData)}
							bodyCellClass={css.cell}
							columns={columns}
							rows={tableData}
						/>
					</div>
				</div>
			</div>

			{deletedObjectWarningModalState.displayDeletedObjectWarningModal ? (
				<DeletedObjectWarningModal
					handleClose={() => setDeletedObjectWarningModalState({ displayDeletedObjectWarningModal: false })}
					message={deletedObjectWarningModalState.message}
				/>
			) : null}
			{deletedObjectConfirmationModalState.displayDeletedObjectConfirmationModal ? (
				<DeletedObjectConfirmationModal
					handleCancel={() =>
						setDeletedObjectConfirmationModalState({ displayDeletedObjectConfirmationModal: false })
					}
					handleConfirm={undeleteReviewReport}
					message="Are you sure you want to undelete this review report?"
					deletedObjectId={deletedObjectConfirmationModalState.deletedObjectId}
				/>
			) : null}
		</>
	);
};

export default DeletedReviewReports;
